import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { Button } from '~/components/ui/buttons/Button';
import { useGetShippingAddOns, useUnhideShippingAddOn } from '~/api/logistics';
import { type ClientError, extractGraphQLErrorMessages } from '~/utils';

interface HiddenAddOnsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HiddenAddOnsModal({ isOpen, onClose }: HiddenAddOnsModalProps) {
  const { t } = useTranslation('logistics');

  const { data, isLoading } = useGetShippingAddOns({ hidden: true });
  const { mutate: unhideAddOn } = useUnhideShippingAddOn();

  const hidden = data?.shippingAddOns ?? [];

  const handleRestore = (id: string) => {
    unhideAddOn(
      { id },
      {
        onSuccess: () =>
          toast.success(t('logistics.add_on_costs.unhide_success')),
        onError: (error: ClientError) =>
          toast.error(extractGraphQLErrorMessages(error).join(', ')),
      }
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-xl">
      <div className="flex flex-col gap-6 p-8 bg-white rounded-lg">
        <div>
          <h2 className="text-lg font-semibold text-neut-prim">
            {t('logistics.add_on_costs.hidden_modal.title')}
          </h2>
          <p className="text-xs text-neut-sec mt-1">
            {t('logistics.add_on_costs.hidden_modal.subtitle')}
          </p>
        </div>

        {isLoading && (
          <p className="text-sm text-neut-sec">
            {t('logistics.add_on_costs.hidden_modal.loading')}
          </p>
        )}

        {!isLoading && hidden.length === 0 && (
          <p className="text-sm text-neut-sec text-center py-6">
            {t('logistics.add_on_costs.hidden_modal.empty')}
          </p>
        )}

        {hidden.length > 0 && (
          <ul className="flex flex-col divide-y divide-neut-border border border-neut-border rounded-lg overflow-hidden">
            {hidden.map((addOn) => (
              <li
                key={addOn.id}
                className="flex items-center justify-between gap-4 px-4 py-3"
                data-testid={`hidden-row-${addOn.id}`}
              >
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-medium text-neut-prim truncate">
                    {addOn.name}
                  </span>
                  <span className="text-xs text-neut-sec truncate">
                    {addOn.category}
                  </span>
                </div>
                <Button
                  size="sm"
                  width="auto"
                  variant="secondary"
                  onClick={() => handleRestore(addOn.id)}
                  data-testid={`hidden-restore-${addOn.id}`}
                >
                  {t('logistics.add_on_costs.unhide')}
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end">
          <Button size="sm" width="auto" variant="secondary" onClick={onClose}>
            {t('logistics.common.close')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
